import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminPageHeader } from '../components/layout/AdminPageHeader';
import { Card, Badge, Button, Input, Modal, Spinner } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { adminApi } from '../services/api';
import { toast } from 'sonner';

const AdminTrucks = () => {
  const { t } = useTranslation();
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    model: '',
    plateNumber: '',
    capacityCylinders: 50,
    driverId: ''
  });

  // Edit vehicle states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: '',
    model: '',
    plateNumber: '',
    capacityCylinders: 50,
    driverId: ''
  });

  // Custom confirm modal states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [trucksData, usersData] = await Promise.all([
        adminApi.getVehicles(),
        adminApi.getUsers()
      ]);
      setTrucks(trucksData || []);
      setDrivers(usersData.filter(u => u.isDriver) || []);
    } catch (err) {
      toast.error(t('adminTrucks.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const formatPlate = (val) => {
    let v = val.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    if (v.length <= 2) return v;
    
    if (/[A-Z]/.test(v[2])) {
      let res = v.slice(0, 2) + ' ' + v[2];
      if (v.length > 3) res += ' ' + v.slice(3, 6);
      if (v.length > 6) res += ' ' + v.slice(6, 8);
      return res.trim();
    } else {
      let res = v.slice(0, 2);
      if (v.length > 2) res += ' ' + v.slice(2, 5);
      if (v.length > 5) res += ' ' + v.slice(5, 8);
      return res.trim();
    }
  };

  const handlePlateChange = (e) => {
    const formatted = formatPlate(e.target.value);
    setFormData({ ...formData, plateNumber: formatted });
  };

  const handleEditPlateChange = (e) => {
    const formatted = formatPlate(e.target.value);
    setEditFormData({ ...editFormData, plateNumber: formatted });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const plateRegex = /^(\d{2}) ([A-Z]) (\d{3}) ([A-Z]{2})$|^(\d{2}) (\d{3}) ([A-Z]{3})$/;
    const plateNoSpaceRegex = /^(\d{2})([A-Z])(\d{3})([A-Z]{2})$|^(\d{2})(\d{3})([A-Z]{3})$/;
    
    const plate = formData.plateNumber.toUpperCase().trim();
    if (!plateRegex.test(plate) && !plateNoSpaceRegex.test(plate)) {
      toast.error(t('adminTrucks.invalidPlateFormat'));
      return;
    }

    try {
      setIsSubmitting(true);
      await adminApi.createVehicle({ ...formData, plateNumber: plate });
      toast.success(t('adminTrucks.added'));
      setIsModalOpen(false);
      setFormData({ model: '', plateNumber: '', capacityCylinders: 50, driverId: '' });
      fetchData();
    } catch (err) {
      toast.error(err.message || t('setPassword.genericError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditModal = (truck) => {
    setEditFormData({
      id: truck.id,
      model: truck.model,
      plateNumber: truck.plateNumber,
      capacityCylinders: truck.capacityCylinders,
      driverId: truck.driverId || ''
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const plateRegex = /^(\d{2}) ([A-Z]) (\d{3}) ([A-Z]{2})$|^(\d{2}) (\d{3}) ([A-Z]{3})$/;
    const plateNoSpaceRegex = /^(\d{2})([A-Z])(\d{3})([A-Z]{2})$|^(\d{2})(\d{3})([A-Z]{3})$/;
    
    const plate = editFormData.plateNumber.toUpperCase().trim();
    if (!plateRegex.test(plate) && !plateNoSpaceRegex.test(plate)) {
      toast.error(t('adminTrucks.invalidPlateFormat'));
      return;
    }

    try {
      setIsSubmitting(true);
      const { id, ...dataToUpdate } = editFormData;
      await adminApi.updateVehicle(id, { ...dataToUpdate, plateNumber: plate });
      toast.success(t('adminTrucks.updated'));
      setIsEditModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || t('setPassword.genericError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerConfirm = (title, message, onConfirm) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleDelete = (id) => {
    triggerConfirm(
      t('adminTrucks.confirmDeleteTitle'),
      t('adminTrucks.confirmDeleteDesc'),
      async () => {
        try {
          await adminApi.deleteVehicle(id);
          toast.success(t('adminTrucks.deleted'));
          fetchData();
        } catch (err) {
          toast.error(err.message);
        }
      }
    );
  };

  if (loading) return <div className="h-[60vh] flex items-center justify-center"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-10 pb-10">
      <AdminPageHeader
        tag="FLEET MANAGEMENT"
        title={t('dashboard.trucksManagement') || 'Transport parki'}
        subtitle={`${trucks.length} ${t('dashboard.registeredTrucks') || 'REGISTERED VEHICLES'}`}
        icon="local_shipping"
      >
        <Button
          variant="emerald"
          onClick={() => setIsModalOpen(true)}
          className="h-14 px-8 rounded-3xl font-black uppercase italic text-xs tracking-widest shadow-xl shadow-emerald-500/20"
        >
          <span className="material-symbols-outlined mr-2">add_circle</span>
          {t('dashboard.addTruck') || 'Yangi transport'}
        </Button>
      </AdminPageHeader>

      {/* Grid of Trucks */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <AnimatePresence>
          {trucks.map((truck, idx) => (
            <motion.div
              key={truck.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className="p-6 rounded-[35px] border-slate-100 dark:border-slate-800/50 hover:border-[#1e9f6b]/30 transition-all group overflow-hidden relative shadow-xl shadow-slate-900/5 bg-white dark:bg-slate-900">
                <div className="absolute top-0 right-0 p-6">
                  <Badge variant={truck.isOnline ? 'green' : 'slate'} pulse={truck.isOnline} className="rounded-xl italic uppercase text-[8px] font-black tracking-widest border-0">
                    {truck.isOnline ? t('adminTrucks.online') : t('adminTrucks.offline')}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-5 mb-8">
                  <div className="h-16 w-16 rounded-2xl bg-[#1e9f6b]/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                    <span className="material-symbols-outlined text-[#1e9f6b] text-4xl">local_shipping</span>
                  </div>
                  <div>
                    <div className="text-2xl font-black italic uppercase text-slate-900 dark:text-white leading-none tracking-tighter">
                      {truck.model}
                    </div>
                    <div className="text-xs font-black text-slate-400 uppercase tracking-widest mt-1 opacity-80">
                      {truck.plateNumber}
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">{t('adminTrucks.capacity')}</span>
                    <span className="text-sm font-black italic text-[#1e9f6b]">{truck.capacityCylinders} {t('common.cylinders')}</span>
                  </div>
                  <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">{t('adminTrucks.driver')}</span>
                    <span className="text-sm font-black italic text-slate-700 dark:text-slate-200 uppercase truncate max-w-[150px]">
                      {truck.driver?.name || truck.driver?.fullName || (truck.driver ? `${truck.driver.firstName} ${truck.driver.lastName}` : t('adminTrucks.unassigned'))}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" className="flex-1 rounded-xl text-[10px] font-black uppercase italic tracking-widest" onClick={() => handleOpenEditModal(truck)}>{t('common.edit')}</Button>
                  <Button variant="danger" size="sm" className="h-11 w-11 p-0 rounded-xl" onClick={() => handleDelete(truck.id)}>
                    <span className="material-symbols-outlined text-sm">delete</span>
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Add Truck Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={t('adminTrucks.addTitle')}
      >
        <form onSubmit={handleCreate} className="space-y-5 py-2">
          <Input
            label={t('adminTrucks.modelLabel')}
            placeholder={t('adminTrucks.modelPlaceholder')}
            value={formData.model}
            onChange={(e) => setFormData({ ...formData, model: e.target.value })}
            required
            className="h-14 font-black italic uppercase text-sm"
          />
          <Input
            label={t('adminTrucks.plateLabel')}
            placeholder="01 A 777 AA"
            value={formData.plateNumber}
            onChange={handlePlateChange}
            required
            className="h-14 font-black italic uppercase text-sm tracking-widest"
            hint={t('adminTrucks.plateFormatHint')}
          />
          <Input
            label={t('adminTrucks.capacityLabel')}
            type="number"
            value={formData.capacityCylinders}
            onChange={(e) => setFormData({ ...formData, capacityCylinders: Number(e.target.value) })}
            required
            className="h-14 font-black italic text-sm"
          />

          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1 italic">{t('adminTrucks.driverLabel')}</label>
            <select
              className="w-full h-14 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 font-black italic uppercase text-xs focus:outline-none focus:border-[#1e9f6b]"
              value={formData.driverId}
              onChange={(e) => setFormData({ ...formData, driverId: e.target.value })}
            >
              <option value="">{t('adminTrucks.driverPlaceholder')}</option>
              {drivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.fullName || driver.name || `${driver.firstName} ${driver.lastName}`} ({driver.phone})
                </option>
              ))}
            </select>
          </div>

          <div className="pt-6 flex gap-3">
             <Button type="button" variant="ghost" onClick={() => setIsModalOpen(false)} className="flex-1 h-14 rounded-2xl font-black italic uppercase tracking-widest text-xs">{t('common.cancel')}</Button>
             <Button type="submit" loading={isSubmitting} className="flex-1 h-14 rounded-2xl font-black italic uppercase tracking-widest text-xs shadow-xl shadow-[#1e9f6b]/20">{t('adminTrucks.add')}</Button>
          </div>
        </form>
      </Modal>

      {/* Edit Truck Modal */}
      <Modal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)}
        title={t('adminTrucks.editTitle')}
      >
        <form onSubmit={handleUpdate} className="space-y-5 py-2">
          <Input
            label={t('adminTrucks.modelLabel')}
            placeholder={t('adminTrucks.modelPlaceholder')}
            value={editFormData.model}
            onChange={(e) => setEditFormData({ ...editFormData, model: e.target.value })}
            required
            className="h-14 font-black italic uppercase text-sm"
          />
          <Input
            label={t('adminTrucks.plateLabel')}
            placeholder="01 A 777 AA"
            value={editFormData.plateNumber}
            onChange={handleEditPlateChange}
            required
            className="h-14 font-black italic uppercase text-sm tracking-widest"
            hint={t('adminTrucks.plateFormatHint')}
          />
          <Input
            label={t('adminTrucks.capacityLabel')}
            type="number"
            value={editFormData.capacityCylinders}
            onChange={(e) => setEditFormData({ ...editFormData, capacityCylinders: Number(e.target.value) })}
            required
            className="h-14 font-black italic text-sm"
          />

          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1 italic">{t('adminTrucks.driverLabel')}</label>
            <select
              className="w-full h-14 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 font-black italic uppercase text-xs focus:outline-none focus:border-[#1e9f6b]"
              value={editFormData.driverId}
              onChange={(e) => setEditFormData({ ...editFormData, driverId: e.target.value })}
            >
              <option value="">{t('adminTrucks.driverPlaceholder')}</option>
              {drivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.fullName || driver.name || `${driver.firstName} ${driver.lastName}`} ({driver.phone})
                </option>
              ))}
            </select>
          </div>

          <div className="pt-6 flex gap-3">
             <Button type="button" variant="ghost" onClick={() => setIsEditModalOpen(false)} className="flex-1 h-14 rounded-2xl font-black italic uppercase tracking-widest text-xs">{t('common.cancel')}</Button>
             <Button type="submit" loading={isSubmitting} className="flex-1 h-14 rounded-2xl font-black italic uppercase tracking-widest text-xs shadow-xl shadow-[#1e9f6b]/20">{t('common.save')}</Button>
          </div>
        </form>
      </Modal>

      {/* Custom Confirm Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        title={confirmModal.title}
        maxWidth="max-w-md"
      >
        <div className="space-y-6 text-center py-4">
          <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-950/30 flex items-center justify-center mx-auto animate-bounce">
            <span className="material-symbols-outlined text-red-500 text-3xl">warning</span>
          </div>
          <p className="text-slate-600 dark:text-slate-400 font-bold italic uppercase text-xs tracking-wider">
            {confirmModal.message}
          </p>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
              className="flex-1 h-14 rounded-2xl font-black italic uppercase tracking-widest text-xs"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={confirmModal.onConfirm}
              className="flex-1 h-14 rounded-2xl font-black italic uppercase tracking-widest text-xs shadow-xl shadow-red-500/20"
            >
              {t('adminTrucks.confirmDelete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminTrucks;